import { useToast } from "@chakra-ui/toast";
import { ascend, includes, prop, sortWith } from "ramda";
import { FC, useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useNavigate } from "react-router";

import { FunnelEditFormContainer_Mutation } from "~/src/__relay_artifacts__/FunnelEditFormContainer_Mutation.graphql";
import { FunnelEditFormContainer_PageMutation } from "~/src/__relay_artifacts__/FunnelEditFormContainer_PageMutation.graphql";
import { FunnelEditFormContainer_Query } from "~/src/__relay_artifacts__/FunnelEditFormContainer_Query.graphql";
import { useFormErrorHandler } from "~/src/lib/hooks";
import { useMutationCommit } from "~/src/lib/react-relay";
import { delayChunkPromise } from "~/src/lib/utils";

import {
  formConvertToPageAttributes,
  fromQueryMatchType,
  hasFormConvertToBoolean,
  responseConvertToForm,
  stringConvertToUrlMatchType,
  toQueryMatchType,
  urlMatchConvertToString,
} from "./convertFunctions";
import { FunnelForm, FunnelFormProps } from "./presentations";

export type Props = {
  siteSlug: string;
  funnelSlug: string;
};

const query = graphql`
  query FunnelEditFormContainer_Query($funnelSlug: String!) {
    viewer {
      role
    }
    funnel(slug: $funnelSlug) {
      id
      slug
      name
      isExperiment
      landingPage {
        id
        name
        url
        mobileUrl
        tabletUrl
        hasForm
        urlMatchType
        queryMatchType
        sortNumber
        isEnableWhiteout
        isSpa
        pageUrlRegex
      }
      thanksPage {
        id
        name
        url
        mobileUrl
        tabletUrl
        urlMatchType
        queryMatchType
        sortNumber
        isEnableWhiteout
        isSpa
        pageUrlRegex
      }
      confirmPage {
        id
        name
        url
        mobileUrl
        tabletUrl
        urlMatchType
        queryMatchType
        sortNumber
        isEnableWhiteout
        isSpa
        pageUrlRegex
      }
      formPage {
        id
        name
        url
        mobileUrl
        tabletUrl
        urlMatchType
        queryMatchType
        sortNumber
        isEnableWhiteout
        isSpa
        pageUrlRegex
      }
    }
  }
`;

const deleteMutation = graphql`
  mutation FunnelEditFormContainer_PageMutation($input: DeletePageInput!) {
    deletePage(input: $input) {
      deletedPageId
    }
  }
`;

const mutation = graphql`
  mutation FunnelEditFormContainer_Mutation($input: UpdateFunnelInput!) {
    updateFunnel(input: $input) {
      funnel {
        id
        slug
        name
        isExperiment
        landingPage {
          id
          name
          url
          mobileUrl
          tabletUrl
          hasForm
          urlMatchType
          queryMatchType
          isEnableWhiteout
          isSpa
          pageUrlRegex
        }
        thanksPage {
          id
          name
          url
          mobileUrl
          tabletUrl
          urlMatchType
          queryMatchType
          isEnableWhiteout
          isSpa
          pageUrlRegex
        }
        confirmPage {
          id
          name
          url
          mobileUrl
          tabletUrl
          urlMatchType
          queryMatchType
          isEnableWhiteout
          isSpa
          pageUrlRegex
        }
        formPage {
          id
          name
          url
          mobileUrl
          tabletUrl
          urlMatchType
          queryMatchType
          isEnableWhiteout
          isSpa
          pageUrlRegex
        }
      }
    }
  }
`;

export const FunnelEditFormContainer: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ siteSlug, funnelSlug }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { onFormError } = useFormErrorHandler();
  const { funnel, viewer } = useLazyLoadQuery<FunnelEditFormContainer_Query>(
    query,
    {
      funnelSlug,
    }
  );
  const updateFunnelMutation =
    useMutationCommit<FunnelEditFormContainer_Mutation>(mutation);

  const deletePageMutate =
    useMutationCommit<FunnelEditFormContainer_PageMutation>(deleteMutation);

  const formPages = useMemo(() => {
    if (!funnel.formPage) return [];
    return sortWith([ascend(prop("sortNumber"))], funnel.formPage);
  }, [funnel.formPage]);

  const forms = useMemo(() => {
    return funnel.formPage?.map((form) => form) || [];
  }, [funnel]);

  const initialValues = useMemo(
    () => ({
      title: funnel?.name || "",
      isExperiment: funnel.isExperiment,
      landingUrlForPC: funnel?.landingPage?.url || "",
      landingUrlForMobile: funnel?.landingPage?.mobileUrl,
      landingUrlForTablet: funnel?.landingPage?.tabletUrl,
      landingUrlMatchType: urlMatchConvertToString(
        funnel?.landingPage?.urlMatchType
      ),
      landingUrlRegularExpression: funnel?.landingPage?.pageUrlRegex || "",
      landingUrlFormIncluded: hasFormConvertToBoolean(
        funnel?.landingPage?.hasForm
      ),
      landingQueryMatchType: fromQueryMatchType(
        funnel?.landingPage?.queryMatchType
      ),
      landingIsEnableWhiteout: funnel?.landingPage?.isEnableWhiteout || false,
      landingIsSpa: funnel?.landingPage?.isSpa || false,
      formUrls: responseConvertToForm(formPages),
      confirmUrlForPC: funnel?.confirmPage?.url || "",
      confirmUrlForMobile: funnel?.confirmPage?.mobileUrl,
      confirmUrlForTablet: funnel?.confirmPage?.tabletUrl,
      confirmUrlMatchType: urlMatchConvertToString(
        funnel?.confirmPage?.urlMatchType
          ? funnel.confirmPage.urlMatchType
          : "EQUAL_TYPE"
      ),
      confirmUrlRegularExpression: funnel?.confirmPage?.pageUrlRegex || "",
      confirmQueryMatchType: fromQueryMatchType(
        funnel?.confirmPage?.queryMatchType
      ),
      confirmIsEnableWhiteout: funnel?.confirmPage?.isEnableWhiteout || false,
      confirmIsSpa: funnel?.confirmPage?.isSpa || false,
      thanksUrlForPC: funnel?.thanksPage?.url || "",
      thanksUrlForMobile: funnel?.thanksPage?.mobileUrl,
      thanksUrlForTablet: funnel?.thanksPage?.tabletUrl,
      thanksUrlMatchType: urlMatchConvertToString(
        funnel?.thanksPage?.urlMatchType
      ),
      thanksUrlRegularExpression: funnel?.thanksPage?.pageUrlRegex || "",
      thanksQueryMatchType: fromQueryMatchType(
        funnel?.thanksPage?.queryMatchType
      ),
      thanksIsEnableWhiteout: funnel?.thanksPage?.isEnableWhiteout || false,
      thanksIsSpa: funnel?.thanksPage?.isSpa || false,
    }),
    [funnel, formPages]
  );

  const handleSubmit = useCallback<FunnelFormProps["onSubmit"]>(
    async (values, { setErrors }) => {
      try {
        if (!funnel) return;

        const formValues = formConvertToPageAttributes(
          values.formUrls,
          funnel.formPage
        );

        const { updateFunnel } = await updateFunnelMutation({
          variables: {
            input: {
              funnelId: funnel.id,
              name: values.title,
              isExperiment: values.isExperiment,
              landingPageAttributes: {
                id: funnel.landingPage?.id,
                name: funnel.landingPage?.name || "ランディングページ",
                url: values.landingUrlForPC,
                mobileUrl: values.landingUrlForMobile,
                tabletUrl: values.landingUrlForTablet,
                hasForm: values.landingUrlFormIncluded ? "HAS_FORM" : "NO_FORM",
                urlMatchType: stringConvertToUrlMatchType(
                  values.landingUrlMatchType
                ),
                queryMatchType: toQueryMatchType(values.landingQueryMatchType),
                isEnableWhiteout: values.landingIsEnableWhiteout,
                isSpa: values.landingIsSpa,
                pageUrlRegex: values.landingUrlRegularExpression || null,
              },
              formPageAttributes: formValues,
              confirmPageAttributes: {
                id: funnel.confirmPage?.id,
                name: funnel.confirmPage?.name || "確認ページ",
                url: values.confirmUrlForPC || "",
                mobileUrl: values.confirmUrlForMobile,
                tabletUrl: values.confirmUrlForTablet,
                urlMatchType: stringConvertToUrlMatchType(
                  values.confirmUrlMatchType
                ),
                queryMatchType: toQueryMatchType(values.confirmQueryMatchType),
                isEnableWhiteout: values.confirmIsEnableWhiteout,
                isSpa: values.confirmIsSpa,
                pageUrlRegex: values.confirmUrlRegularExpression || null,
              },
              thanksPageAttributes: {
                id: funnel.thanksPage?.id,
                name: funnel.thanksPage?.name || "サンクスページ",
                url: values.thanksUrlForPC,
                mobileUrl: values.thanksUrlForMobile,
                tabletUrl: values.thanksUrlForTablet,
                urlMatchType: stringConvertToUrlMatchType(
                  values.thanksUrlMatchType
                ),
                queryMatchType: toQueryMatchType(values.thanksQueryMatchType),
                isEnableWhiteout: values.thanksIsEnableWhiteout,
                isSpa: values.thanksIsSpa,
                pageUrlRegex: values.thanksUrlRegularExpression || null,
              },
            },
          },
        });
        if (!updateFunnel?.funnel) throw new Error("assertion failed");

        // MEMO: undefined is new page
        const formIds: string[] = [];
        formValues.forEach((formValue) => {
          const id = formValue.id?.toString();
          if (id) {
            formIds.push(id);
          }
        });

        const deleteTargetFormPageIds = forms
          .filter((form) => !includes(form.id, formIds))
          .map((form) => form.id);

        const deleteMutations: Promise<{}>[] = deleteTargetFormPageIds.map(
          (id) => {
            return deletePageMutate({
              variables: {
                input: {
                  pageId: id,
                },
              },
            });
          }
        );
        await delayChunkPromise(deleteMutations);

        toast({ title: "ファネルを更新しました", status: "success" });
        navigate(`/sites/${siteSlug}/settings?tab=funnels`);
      } catch (err) {
        onFormError(err, setErrors);
      }
    },
    [
      funnel,
      updateFunnelMutation,
      forms,
      toast,
      navigate,
      siteSlug,
      deletePageMutate,
      onFormError,
    ]
  );

  const handleCancel = useCallback(() => navigate(-1), [navigate]);

  return (
    <FunnelForm
      initialValues={initialValues}
      onCancelClick={handleCancel}
      onSubmit={handleSubmit}
      userRole={viewer.role}
    />
  );
};
